import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { HERO_VIDEO } from "@/lib/site";

export default function HeroVideo() {
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [videoFailed, setVideoFailed] = useState(false);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0.35]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (reduce) {
      v.pause();
      setPlaying(false);
    }
  }, [reduce]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      ref={sectionRef}
      className="relative flex min-h-[640px] items-center overflow-hidden bg-navy"
      style={{ height: "100svh", maxHeight: "980px" }}
      data-testid="hero-section"
    >
      {!videoFailed && (
        <motion.div style={{ y: videoY }} className="absolute inset-0 will-change-transform">
          <video
            ref={videoRef}
            className="h-[118%] w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={HERO_VIDEO.poster}
            aria-hidden="true"
            tabIndex={-1}
            onError={() => setVideoFailed(true)}
            data-testid="hero-video"
          >
            <source src={HERO_VIDEO.src} type="video/mp4" media="(min-width: 768px)" />
            <source src={HERO_VIDEO.mobileSrc} type="video/mp4" />
          </video>
        </motion.div>
      )}

      {videoFailed && (
        <img
          src={HERO_VIDEO.poster}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="hero-fallback-image"
        />
      )}

      {/* Dark navy readability gradient: darkest behind the type, fully bright on the right */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/60 to-navy/0"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-navy/80 to-transparent"
      />

      {/* RIVERSTONE name watermark — the opening moment built around the name */}
      <motion.div
        aria-hidden="true"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute -bottom-2 right-0 z-[5] select-none whitespace-nowrap font-serif text-[14vw] leading-none tracking-[0.16em] text-ivory/[0.12] sm:text-[11vw] lg:text-[8.5vw]"
        data-testid="hero-name-watermark"
      >
        RIVERSTONE
      </motion.div>

      <motion.div style={{ opacity: fade }} className="shell relative z-10 pb-20 pt-32">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="eyebrow text-[#9FC1D4] text-shadow-hero"
            data-testid="hero-eyebrow"
          >
            Business Solutions. Lasting Partnerships.
          </motion.p>

          <h1 className="text-shadow-hero mt-6 font-serif text-4xl font-light leading-[1.08] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["A stronger business.", "A clearer path forward."]} delay={0.25} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="text-shadow-hero mt-6 max-w-xl text-base leading-relaxed text-ivory/90 sm:text-lg"
            data-testid="hero-supporting-copy"
          >
            Discover practical ways to grow revenue, improve efficiency, support your
            people, and explore new opportunities—with Riverstone Partners helping you
            take the next step.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col gap-3 sm:flex-row sm:gap-4"
          >
            <Link to="/solutions" className="btn-primary border border-ivory/70 bg-ivory text-navy shadow-lift hover:bg-sage" data-testid="hero-cta-solutions">
              Explore Our Solutions
            </Link>
            <Link to="/contact" className="btn-ghost-light glass-panel border-ivory/70 shadow-lift" data-testid="hero-cta-lets-talk">
              Let’s Talk
            </Link>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="text-shadow-hero mt-10 text-[11px] font-medium uppercase tracking-[0.24em] text-ivory/70"
            data-testid="hero-supporting-line"
          >
            Growth · Efficiency · Employee Wellbeing · Opportunity
          </motion.p>
        </div>
      </motion.div>

      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? "Pause background video" : "Play background video"}
        aria-pressed={!playing}
        data-testid="hero-video-toggle"
        className="absolute bottom-6 right-6 z-20 flex h-11 w-11 items-center justify-center glass-panel border border-ivory/50 text-ivory/90 transition-colors hover:border-ivory hover:text-ivory"
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
    </section>
  );
}
